<script setup lang="ts">
const localePath = useLocalePath()
const { config } = useFrontendConfiguration()
const { t } = useI18n()

const cookieConsentGiven = useCookie<boolean>('cookie_consent_given')
</script>

<template>
  <div class="min-h-screen flex flex-col">
    <ScrollTop />
    <div class="min-h-2 bg-primary-700" />
    <div class="bg-primary py-4 px-6 md:px-12 lg:px-20 flex items-center justify-between relative lg:static min-h-20">
      <NuxtLink
        :to="localePath('landing')"
      >
        <img
          src="/logo_white.svg"
          class="mr-0 lg:mr-12 w-auto h-7"
          alt="Logo"
        >
      </NuxtLink>
      <button
        v-styleclass="{
          selector: '@next',
          enterFromClass: 'hidden',
          leaveToClass: 'hidden',
          hideOnOutsideClick: true,
        }"
        class="block lg:hidden text-primary-contrast pt-1"
      >
        <i class="pi pi-bars text-2xl" />
      </button>
      <div
        class="items-center grow justify-between hidden lg:flex absolute lg:static w-full bg-surface-0 lg:bg-transparent left-0 top-full z-10 border-b lg:border-none"
      >
        <ul class="list-none p-0 m-0 flex lg:items-center select-none flex-col lg:flex-row">
          <li class="mr-2">
            <NuxtLink
              exact-active-class="lg:bg-primary-emphasis bg-surface-100"
              :to="localePath('landing')"
              class="flex px-12 p-4 lg:px-4 lg:py-2 items-center text-surface-700 lg:text-primary-contrast hover:bg-surface-100 lg:hover:bg-primary-emphasis font-medium lg:rounded-border transition-colors duration-150"
            >
              <span>{{ t("Startseite") }}</span>
            </NuxtLink>
          </li>
          <li class="mr-2">
            <NuxtLink
              exact-active-class="lg:bg-primary-emphasis bg-surface-100"
              :to="localePath('landing-employer')"
              class="flex px-12 p-4 lg:px-4 lg:py-2 items-center text-surface-700 lg:text-primary-contrast hover:bg-surface-100 lg:hover:bg-primary-emphasis font-medium lg:rounded-bordertransition-colors duration-150"
            >
              <span>{{ t("Arbeitgeber") }}</span>
            </NuxtLink>
          </li>
          <li class="mr-2">
            <NuxtLink
              exact-active-class="lg:bg-primary-emphasis bg-surface-100"
              :to="localePath('landing-candidate')"
              class="flex px-12 p-4 lg:px-4 lg:py-2 items-center text-surface-700 lg:text-primary-contrast hover:bg-surface-100 lg:hover:bg-primary-emphasis font-medium lg:rounded-border transition-colors duration-150"
            >
              <span>{{ t("Kandidaten") }}</span>
            </NuxtLink>
          </li>
        </ul>
        <div class="lg:p-0 px-12 py-6">
          <LandingLanguageSelect
            class="w-full lg:w-56"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col flex-auto bg-surface-50 py-6 md:py-8 px-6 md:px-12 lg:px-20">
      <slot />
    </div>
    <div>
      <div class="flex lg:flex-row flex-col bg-primary py-8 px-6 md:px-12 lg:px-20">
        <div class="w-full flex-1">
          <div>
            <img
              src="/logo_white.svg"
              class="w-auto h-10"
              alt="Logo"
            >
          </div>
          <span class="text-surface-0 block mt-4">{{ t("Weil du mehr bist als ein Lebenslauf – entdecke das Job-Portal, das deine Persönlichkeit in den Mittelpunkt stellt.") }}</span>
          <LandingCountrySelect class="mt-4 w-full lg:w-56" />
          <span class="text-surface-400 block mt-4">© {{ getCurrentYear() }} {{ config!.name }}</span>
        </div>
        <div class="h-px w-full lg:w-px lg:h-auto bg-surface-400 my-8 lg:my-0 lg:mx-8 mx-0" />
        <div class="flex-1 flex gap-8 lg:gap-16 sm:flex-row flex-col">
          <div class="w-full flex-1 flex flex-col">
            <span class="text-surface-0 text-xl font-medium block">{{ t("Für Arbeitgeber") }}</span>
            <ul class="list-none p-0 flex flex-col gap-2 mt-4">
              <li>
                <NuxtLink
                  :to="localePath('landing-employer')"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Arbeitgeberbereich") }}</NuxtLink>
              </li>
            </ul>
            <span class="text-surface-0 text-xl font-medium block mt-8">{{ t("Karrierebereiche") }}</span>
            <ul class="list-none p-0 flex flex-col gap-2 mt-4">
              <li>
                <NuxtLink
                  :to="localePath('landing-realestate')"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Immobilien") }}</NuxtLink>
              </li>
            </ul>
          </div>
          <div class="w-full flex-1 flex flex-col">
            <span class="text-surface-0 text-xl font-medium block">{{ t("Für Kandidaten") }}</span>
            <ul class="list-none p-0 flex flex-col gap-2 mt-4">
              <li>
                <NuxtLink
                  :to="localePath('landing-candidate')"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Kandidatenbereich") }}</NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="`${localePath('landing')}`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Start") }}</NuxtLink>
              </li>
              <!-- <li>
                <NuxtLink
                  :to="`${localePath('landing')}#premiumemployers`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Premium-Arbeitgeber") }}</NuxtLink>
              </li> -->
              <li>
                <NuxtLink
                  :to="`${localePath('landing')}#howitworks`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("So funktioniert's") }}</NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="`${localePath('landing')}#getstartednow`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Jetzt loslegen") }}</NuxtLink>
              </li>
              <!-- <li>
                <NuxtLink
                  :to="`${localePath('landing')}#examples`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Beispiele") }}</NuxtLink>
              </li> -->
              <li>
                <NuxtLink
                  :to="`${localePath('landing')}#privacy`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Datenschutz") }}</NuxtLink>
              </li>
              <!-- <li>
                <NuxtLink
                  :to="`${localePath('landing')}#testimonials`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Erfahrungsberichte") }}</NuxtLink>
              </li> -->
              <li>
                <NuxtLink
                  :to="`${localePath('landing')}#frequentlyaskedquestions`"
                  class="text-primary-contrast hover:opacity-75 transition-all"
                >{{ t("Häufig gestellte Fragen") }}</NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="bg-primary-700 flex lg:items-center lg:flex-row flex-col justify-between gap-8 p-6 md:px-12 lg:px-20">
        <div class="flex justify-center gap-4">
          <a
            title="TikTok"
            href="https://www.tiktok.com/@introfy.app"
            target="_blank"
            class="w-6 h-6 bg-surface-200 hover:opacity-75 transition-all flex items-center justify-center rounded-full"
          >
            <i class="pi pi-tiktok text-sm text-surface-900 leading-none" />
          </a>
          <a
            title="Facebook"
            href="https://www.facebook.com/introfyapp/"
            target="_blank"
            class="w-6 h-6 bg-surface-200 hover:opacity-75 transition-all flex items-center justify-center rounded-full"
          >
            <i class="pi pi-facebook text-sm text-surface-900 leading-none" />
          </a>
          <a
            title="YouTube"
            href="https://www.youtube.com/@IntrofyApp"
            target="_blank"
            class="w-6 h-6 bg-surface-200 hover:opacity-75 transition-all flex items-center justify-center rounded-full"
          >
            <i class="pi pi-youtube text-sm text-surface-900 leading-none" />
          </a>
          <a
            title="Instagram"
            href="https://www.instagram.com/introfy.app/"
            target="_blank"
            class="w-6 h-6 bg-surface-200 hover:opacity-75 transition-all flex items-center justify-center rounded-full"
          >
            <i class="pi pi-instagram text-sm text-surface-900 leading-none" />
          </a>
          <a
            title="X"
            href="https://x.com/IntrofyApp"
            target="_blank"
            class="w-6 h-6 bg-surface-200 hover:opacity-75 transition-all flex items-center justify-center rounded-full"
          >
            <i class="pi pi-twitter text-sm text-surface-900 leading-none" />
          </a>
        </div>
        <ul class="list-none flex flex-col lg:flex-row items-center gap-4 lg:gap-8">
          <li>
            <NuxtLink
              :to="localePath('landing-pricing')"
              class="text-primary-contrast hover:opacity-75 transition-all "
            >{{ t("Preise") }}</NuxtLink>
          </li>
          <li>
            <NuxtLink
              :to="localePath('landing-terms')"
              class="text-primary-contrast hover:opacity-75 transition-all "
            >{{ t("AGB") }}</NuxtLink>
          </li>
          <li>
            <button
              class="text-primary-contrast hover:opacity-75 transition-all "
              @click="cookieConsentGiven = false"
            >
              {{ t("Cookies") }}
            </button>
          </li>
          <li>
            <NuxtLink
              :to="localePath('landing-privacy')"
              class="text-primary-contrast hover:opacity-75 transition-all "
            >{{ t("Datenschutz") }}</NuxtLink>
          </li>
          <li>
            <NuxtLink
              :to="localePath('landing-imprint')"
              class="text-primary-contrast hover:opacity-75 transition-all "
            >
              {{ t("Impressum") }}
            </NuxtLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
